// src/pages/ChangePassword.tsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { apiPost } from "../api/client";
import { Button } from "../components/Button";
import { A } from "../styles/appleClasses";

type Props = {
  mode?: "page" | "inlay";
  onClose?: () => void;
};

export default function ChangePassword({ mode = "inlay", onClose }: Props) {
  const { t } = useTranslation();
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");

  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  type ErrorKey = "changePassword.fieldsRequired" | "changePassword.passwordMismatch" | "changePassword.failed" | "changePassword.oldPasswordIncorrect" | "changePassword.passwordTooShort" | "accountInlays.errors.tooManyRequests" | "accountInlays.errors.inactiveUser" | "accountInlays.errors.staffProRequired";
  const [error, setError] = useState<{ key: ErrorKey } | { raw: any } | null>(null);
  const displayedError = error && ("key" in error ? t(error.key) : error.raw);

  const navigate = useNavigate();

  const handleClose = () => {
    if (mode === "inlay") onClose?.();
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg("");
    setError(null);

    if (!currentPw || !newPw || !confirmPw) {
      setError({ key: "changePassword.fieldsRequired" });
      return;
    }
    if (newPw !== confirmPw) {
      setError({ key: "changePassword.passwordMismatch" });
      return;
    }

    setSaving(true);
    try {
      await apiPost("/auth/change-password", {
        old_password: currentPw,
        new_password: newPw,
      });

      // Patch 4i: the backend invalidates every previously-issued token
      // (including this tab's own) the moment change-password succeeds.
      // Without this, the very next API call from this tab would 401 and
      // hit client.ts's handle401(), which redirects to /login?expired=1
      // -- misleading messaging for a deliberate, successful action, not
      // a timeout. Redirect proactively instead, with a distinct query
      // param so Login.tsx can show an accurate message.
      localStorage.removeItem("access_token");
      navigate("/login?password_changed=1");
      return;
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 400 && detail === "Old password incorrect") {
        setError({ key: "changePassword.oldPasswordIncorrect" });
      } else if (status === 400 && detail === "Password too short.") {
        setError({ key: "changePassword.passwordTooShort" });
      } else if (status === 429 && detail === "Too many requests. Please try again later.") {
        setError({ key: "accountInlays.errors.tooManyRequests" });
      } else if (status === 403 && detail === "Inactive user.") {
        setError({ key: "accountInlays.errors.inactiveUser" });
      } else if (status === 403 && detail === "Staff access requires an active Pro subscription.") {
        setError({ key: "accountInlays.errors.staffProRequired" });
      } else if (Array.isArray(detail)) {
        setError({
          raw: detail
            .map((x: any) => x?.msg)
            .filter(Boolean)
            .join(", "),
        });
      } else {
        setError(detail ? { raw: detail } : { key: "changePassword.failed" });
      }
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <form onSubmit={handleSave} className="space-y-4">
      <div>
        <label className={`block mb-1 ${A.label}`}>{t("changePassword.currentPassword")}</label>
        <input
          type="password"
          className={A.inputBase}
          value={currentPw}
          onChange={(e) => setCurrentPw(e.target.value)}
          autoComplete="current-password"
        />
      </div>

      <div>
        <label className={`block mb-1 ${A.label}`}>{t("resetPassword.newPassword")}</label>
        <input
          type="password"
          className={A.inputBase}
          value={newPw}
          onChange={(e) => setNewPw(e.target.value)}
          autoComplete="new-password"
        />
      </div>

      <div>
        <label className={`block mb-1 ${A.label}`}>{t("changePassword.confirmNewPassword")}</label>
        <input
          type="password"
          className={A.inputBase}
          value={confirmPw}
          onChange={(e) => setConfirmPw(e.target.value)}
          autoComplete="new-password"
        />
      </div>

      <Button
        className="w-full justify-center"
        type="submit"
        variant="primary"
        disabled={saving}
      >
        {saving ? t("directory.common.savingDots") : t("directory.common.save")}
      </Button>

      {/* Reserve space to avoid layout jump */}
      <div style={{ minHeight: 24 }}>
        {msg && (
          <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-[13px] text-green-700">
            {msg}
          </div>
        )}
        {displayedError && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
            {displayedError}
          </div>
        )}
      </div>
    </form>
  );

  if (mode === "inlay") {
    return (
      <div
        className="fixed inset-0 z-[1000] bg-black/40 overflow-y-auto"
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) handleClose();
        }}
      >
        <div className="min-h-full flex items-start justify-center p-4 pt-16 pb-10">
          <div className={`w-full max-w-md ${A.modal} overflow-visible`}>
            {/* Centered title header */}
            <div className="grid grid-cols-[40px_1fr_40px] items-center px-5 py-4 border-b border-gray-100">
              <div />
              <h3 className={`${A.title} text-center`}>{t("accountMenu.changePassword")}</h3>
              <button
                onClick={handleClose}
                className={[
                  A.iconBtn,
                  "justify-self-end inline-flex items-center justify-center",
                  "hover:bg-gray-100",
                  "focus:outline-none focus:ring-2 focus:ring-appleblueD",
                ].join(" ")}
                aria-label={t("directory.common.close")}
                type="button"
              >
                <XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 transition" />
              </button>
            </div>

            <div className={`px-6 py-6 ${A.body}`}>{content}</div>
          </div>
        </div>
      </div>
    );
  }

  // Page mode
  return (
    <div
      className={`min-h-screen bg-gray-50 ${A.body} flex justify-center px-2 py-6`}
    >
      <div className={`w-full max-w-md ${A.card} p-6`}>
        <div className="text-center mb-4">
          <h2 className={A.title}>{t("accountMenu.changePassword")}</h2>
        </div>
        {content}
      </div>
    </div>
  );
}
